import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, AlertCircle, Edit2 } from 'lucide-react';
import axios from 'axios';
import { getAuthHeaders } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import { createPortal } from 'react-dom';

const API = process.env.REACT_APP_BACKEND_URL;

function calcAge(dob) {
  if (!dob) return '';
  const birth = new Date(dob);
  const now = new Date();
  let age = now.getFullYear() - birth.getFullYear();
  const m = now.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && now.getDate() < birth.getDate())) age--;
  return age >= 0 ? age : '';
}

function fmtDT(iso) {
  if (!iso) return 'Never';
  return new Date(iso).toLocaleString('en-IN', {
    day: '2-digit', month: 'long', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
}

const DECL = [
  'I confirm that the above filled information is true to the best of my knowledge.',
  'I agree that the teacher will always guide me to the best of her ability and any injury due to exercises/asanas during class will not be the responsibility of the teacher.',
  'I agree that the fees once paid is non-refundable.',
];

const INPUT = 'bg-[#FFFAF0] border-2 border-[#E7E5E4] rounded-2xl px-4 py-3 focus:ring-4 focus:ring-[#F59E0B]/20 focus:border-[#F59E0B] outline-none w-full text-[#292524] font-body text-sm';

function RO({ v }) {
  return <p className="text-[#292524] font-body text-sm bg-[#FFFAF0] border border-[#E7E5E4] rounded-2xl px-4 py-3 min-h-[44px]">{v || '—'}</p>;
}
function Lbl({ t }) { return <p className="text-xs font-medium text-[#57534E] mb-1.5">{t}</p>; }
function SectionCard({ title, children }) {
  return (
    <div className="bg-white rounded-3xl p-6 shadow-card border border-[#E7E5E4]/50">
      <h3 className="font-heading font-semibold text-sm uppercase tracking-widest text-[#F59E0B] mb-4 pb-2 border-b border-[#F5F5F4]">{title}</h3>
      {children}
    </div>
  );
}

function Toast({ message, type, onClose }) {
  useEffect(() => { const t = setTimeout(onClose, 3500); return () => clearTimeout(t); }, [onClose]);
  return createPortal(
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-5 py-3.5 rounded-2xl shadow-lg text-sm font-body
      ${type === 'success' ? 'bg-green-50 border border-green-200 text-green-700' : 'bg-red-50 border border-red-200 text-red-700'}`}>
      {type === 'success' ? <CheckCircle className="w-4 h-4 shrink-0" /> : <AlertCircle className="w-4 h-4 shrink-0" />}
      {message}
    </div>,
    document.body
  );
}

export default function AdminStudentProfileView() {
  const { studentId } = useParams();
  const navigate = useNavigate();
  const { user: admin } = useAuth();

  const [student, setStudent] = useState(null);
  const [profile, setProfile] = useState(null);
  const [batchName, setBatchName] = useState('—');
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState(null);

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [studentsRes, batchesRes, profileRes] = await Promise.all([
          axios.get(`${API}/api/admin/students`, { headers: getAuthHeaders() }),
          axios.get(`${API}/api/admin/batches`, { headers: getAuthHeaders() }),
          axios.get(`${API}/api/admin/profile-details/${studentId}`, { headers: getAuthHeaders() }),
        ]);
        const s = studentsRes.data.find(x => x.id === studentId);
        if (s) {
          setStudent(s);
          if (s.batch_id) {
            const b = batchesRes.data.find(b => b.id === s.batch_id);
            if (b) setBatchName(b.name);
          }
        }
        if (profileRes.data) {
          setProfile(profileRes.data);
          setForm({
            email: profileRes.data.email || '',
            date_of_birth: profileRes.data.date_of_birth || '',
            joining_date: profileRes.data.joining_date || '',
            emergency_contact: profileRes.data.emergency_contact || '',
            address: profileRes.data.address || '',
            medications: profileRes.data.medications || '',
            health_concerns: profileRes.data.health_concerns || '',
          });
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    fetchAll();
  }, [studentId]);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/api/admin/profile-details/${studentId}`, form, { headers: getAuthHeaders() });
      setProfile(res.data);
      setEditing(false);
      setToast({ message: 'Profile Details updated successfully.', type: 'success' });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      setToast({ message: typeof detail === 'string' ? detail : 'Something went wrong.', type: 'error' });
    } finally { setSaving(false); }
  };

  const age = calcAge(editing ? form.date_of_birth : profile?.date_of_birth);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-20">
        <div className="w-10 h-10 border-4 border-[#F59E0B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up max-w-4xl mx-auto space-y-6">
      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {/* Header */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <button
            onClick={() => navigate('/admin/profiles')}
            className="flex items-center gap-1.5 text-sm text-[#57534E] hover:text-[#292524] font-body transition-colors mb-3"
          >
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} />
            Back to Student Profiles
          </button>
          <p className="text-xs font-semibold uppercase tracking-widest text-[#F59E0B] font-body mb-1">Profile Details</p>
          <h1 className="font-heading text-2xl sm:text-3xl font-medium text-[#292524]">
            {student?.name || 'Student Profile'}
          </h1>
          <p className="text-sm text-[#78716C] font-body mt-1">
            {student?.phone}
            {batchName !== '—' && ` · ${batchName}`}
            {student?.is_archived && <span className="ml-2 text-gray-400 text-xs">(Archived)</span>}
          </p>
        </div>
        {profile && !editing && (
          <button
            onClick={() => setEditing(true)}
            className="flex items-center gap-2 bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full px-5 py-2.5 text-sm font-heading font-medium shadow-button hover:-translate-y-0.5 transition-all"
          >
            <Edit2 className="w-4 h-4" strokeWidth={1.5} /> Edit Profile
          </button>
        )}
      </div>

      {!profile ? (
        <div className="bg-white rounded-3xl p-12 text-center shadow-card border border-[#E7E5E4]/50">
          <p className="font-heading font-medium text-[#292524]">Profile not submitted yet</p>
          <p className="text-xs text-[#78716C] font-body mt-1">This student has not completed the mandatory Profile Details form.</p>
        </div>
      ) : (
        <>
          {/* Personal Details */}
          <SectionCard title="Personal Details">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div><Lbl t="Name" /><RO v={student?.name} /></div>
              <div><Lbl t="Mobile Number" /><RO v={student?.phone} /></div>
              <div>
                <Lbl t="Email" />
                {editing
                  ? <input value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className={INPUT} />
                  : <RO v={profile.email} />}
              </div>
              <div>
                <Lbl t="Joining Date" />
                {editing
                  ? <input type="date" value={form.joining_date} onChange={e => setForm(f => ({ ...f, joining_date: e.target.value }))} className={INPUT} />
                  : <RO v={profile.joining_date} />}
              </div>
              <div>
                <Lbl t="Date of Birth" />
                {editing
                  ? <input type="date" value={form.date_of_birth} onChange={e => setForm(f => ({ ...f, date_of_birth: e.target.value }))} className={INPUT} />
                  : <RO v={profile.date_of_birth} />}
              </div>
              <div><Lbl t="Age (Auto-calculated)" /><RO v={age ? `${age} years` : '—'} /></div>
              <div>
                <Lbl t="Emergency Contact Number" />
                {editing
                  ? <input value={form.emergency_contact} onChange={e => setForm(f => ({ ...f, emergency_contact: e.target.value }))} className={INPUT} />
                  : <RO v={profile.emergency_contact} />}
              </div>
            </div>
            <div className="mt-4">
              <Lbl t="Address" />
              {editing
                ? <textarea rows={2} value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className={`${INPUT} resize-none`} />
                : <RO v={profile.address} />}
            </div>
          </SectionCard>

          {/* Medical Details */}
          <SectionCard title="Medical Details">
            <div className="space-y-3">
              <div>
                <Lbl t="Ongoing / Recent Medications" />
                {editing
                  ? <textarea rows={2} value={form.medications} onChange={e => setForm(f => ({ ...f, medications: e.target.value }))} className={`${INPUT} resize-none`} />
                  : <RO v={profile.medications} />}
              </div>
              <div>
                <Lbl t="Health Concerns" />
                {editing
                  ? <textarea rows={2} value={form.health_concerns} onChange={e => setForm(f => ({ ...f, health_concerns: e.target.value }))} className={`${INPUT} resize-none`} />
                  : <RO v={profile.health_concerns} />}
              </div>
            </div>
          </SectionCard>

          {/* Declarations */}
          <SectionCard title="Declarations">
            {DECL.map((text, i) => (
              <div key={i} className="flex items-start gap-3 bg-green-50/50 rounded-2xl p-3 border border-green-100 mb-2 last:mb-0">
                <CheckCircle className="w-4 h-4 text-green-600 shrink-0 mt-0.5" strokeWidth={2} />
                <p className="text-xs text-[#292524] font-body leading-relaxed">{text}</p>
              </div>
            ))}
          </SectionCard>

          {/* Signature */}
          <SectionCard title="Signature">
            {profile.signature_data
              ? <img src={profile.signature_data} alt="Student Signature" className="rounded-2xl border border-[#E7E5E4] max-w-sm bg-white" />
              : <p className="text-[#A8A29E] font-body text-sm">No signature on file</p>
            }
          </SectionCard>

          {/* Audit Information */}
          <SectionCard title="Audit Information">
            <div className="space-y-2 text-xs font-body">
              <p><span className="text-[#A8A29E]">Profile Completed On: </span><strong className="text-[#292524]">{fmtDT(profile.submitted_at)}</strong></p>
              <p><span className="text-[#A8A29E]">Last Updated On: </span><strong className="text-[#292524]">{profile.last_updated_at ? fmtDT(profile.last_updated_at) : 'Never'}</strong></p>
              {profile.last_updated_at && (
                <p><span className="text-[#A8A29E]">Last Updated By: </span><strong className="text-[#292524]">{profile.last_updated_by || '—'}</strong></p>
              )}
            </div>
          </SectionCard>

          {/* Edit buttons */}
          {editing && (
            <div className="flex gap-3 pb-6">
              <button onClick={() => setEditing(false)}
                className="border-2 border-[#E7E5E4] text-[#57534E] rounded-full py-3 px-6 font-heading font-medium text-sm hover:border-[#F59E0B] transition-colors">
                Cancel
              </button>
              <button onClick={handleSave} disabled={saving}
                className="bg-[#F59E0B] hover:bg-[#D97706] text-white rounded-full py-3 px-6 font-heading font-medium text-sm shadow-button disabled:opacity-60 transition-all">
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          )}
        </>
      )}
    </div>
  );
}
